import React, { useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, Tag, Spin, Row, Space, Input } from "antd";
import {
    LoadingOutlined,
    ArrowLeftOutlined,
    FilePdfOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { usePDF } from "react-to-pdf";
import { Segmented } from "../../../design-system";
import {
    BuyerBrief,
    BuyerBriefIntake,
    BriefStatus,
    BriefJobStatus,
    BuyerBriefJob,
    Verdict,
    VERDICT_LABELS,
} from "../../../types/BuyerBriefTypes";
import { DEMO_BRIEFS, DEMO_JOBS } from "./demoData";
import { BuyerBriefCard } from "../components/BuyerBriefCard";
import { BuyerBriefJobCard } from "../components/BuyerBriefJobCard";
import DemoBriefWizard from "./DemoBriefWizard";
import classes from "./DemoBuyerBriefs.module.less";

const BADGE_COLORS: Record<string, string> = {
    [Verdict.STRONG_YES]: "green",
    [Verdict.YES]: "green",
    [Verdict.CONDITIONAL]: "gold",
    [Verdict.RENEGOTIATE]: "orange",
    [Verdict.NO]: "red",
    [Verdict.STRONG_NO]: "magenta",
};

const JOB_STEP_LABELS: Record<string, string> = {
    [BriefJobStatus.QUEUED]: "Queued for processing...",
    [BriefJobStatus.RESEARCHING]: "Conducting AI research...",
    [BriefJobStatus.VERIFYING]: "Verifying research quality...",
    [BriefJobStatus.RENDERING]: "Rendering brief...",
};

let nextDemoId = 100;
let nextJobId = 1000;

enum TabKey {
    Existing = "existing",
    Pending = "pending",
}

const TabOptions = [
    {
        label: "Existing",
        value: TabKey.Existing,
    },
    {
        label: "Pending",
        value: TabKey.Pending,
    },
];

export const DemoBriefListView: React.FC = () => {
    const navigate = useNavigate();
    const [briefs, setBriefs] = useState<BuyerBrief[]>([...DEMO_BRIEFS]);
    const [wizardOpen, setWizardOpen] = useState(false);

    const handleWizardSubmit = (intake: BuyerBriefIntake) => {
        const newId = nextDemoId++;
        const newBrief: BuyerBrief = {
            id: newId,
            organizationId: 1,
            creatorId: 1,
            status: BriefStatus.PROCESSING,
            supplierName: intake.supplierName,
            supplierWebsite: intake.supplierWebsite,
            supplierType: intake.supplierType,
            evaluationGoal: intake.evaluationGoal,
            selectedTopics: intake.selectedTopics,
            selectedAddons: intake.selectedAddons,
            intakePayload: intake,
            webSearchesExecuted: 0,
            recordCreationDate: new Date().toISOString(),
        };

        const newJobId = nextJobId++;
        const newJob: BuyerBriefJob = {
            id: newJobId,
            organizationId: 1,
            briefId: newId,
            jobType: "buyer_brief_v2",
            supplierName: intake.supplierName,
            status: BriefJobStatus.QUEUED,
            startTime: new Date().toISOString(),
            retryCount: 0,
        };
        setBriefs((prev) => [newBrief, ...prev]);
        setPendingJobs((prev) => [newJob, ...prev]);
    };
    const [pendingJobs, setPendingJobs] = useState<BuyerBriefJob[]>([...DEMO_JOBS]);
    const [tabKey, setTabKey] = useState(TabKey.Existing);
    const [search, setSearch] = useState("");

    const displayedBriefs = briefs.filter((b) =>
        b.supplierName.toLowerCase().includes(search.toLowerCase())
    );

    const content =
        tabKey === TabKey.Existing
            ? displayedBriefs.map((b) => (
                  <BuyerBriefCard
                      key={b.id}
                      brief={b}
                      onClick={() => navigate(`/superadmin/demo/brief/${b.id}`)}
                  />
              ))
            : pendingJobs.map((j) => <BuyerBriefJobCard key={j.id} job={j} />);

    return (
        <Card bordered={false}>
            <Row justify="space-between" className={classes.header}>
                <div>
                    <div className={classes.title}>Buyer Briefs</div>
                    <div className={classes.subtitle}>
                        Vendor diligence reports covering market position, competitive landscape,
                        and risk factors for each vendor.
                    </div>
                </div>
                <Button
                    type="primary"
                    size="large"
                    className={classes.creator}
                    onClick={() => setWizardOpen(true)}
                >
                    + Buyer Brief
                </Button>
            </Row>
            <Space align="center" size={16} className={classes.tools}>
                <Segmented options={TabOptions} onChange={(val) => setTabKey(val as TabKey)} />
                {tabKey === TabKey.Existing && (
                    <Input
                        className={classes.search}
                        placeholder="Search..."
                        prefix={<SearchOutlined />}
                        allowClear
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                )}
            </Space>
            {content}
            <DemoBriefWizard
                open={wizardOpen}
                onClose={() => setWizardOpen(false)}
                onSubmit={handleWizardSubmit}
            />
        </Card>
    );
};

export const DemoBriefDetailView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const htmlContainerRef = useRef<HTMLDivElement>(null);

    const brief = DEMO_BRIEFS.find((b) => b.id === Number(id));

    const { toPDF, targetRef } = usePDF({
        filename: `buyer-brief-${brief?.supplierName || id}.pdf`,
    });

    if (!brief) {
        return (
            <Card>
                <p>Brief not found.</p>
                <Button onClick={() => navigate("/superadmin/demo/brief")}>Back to Briefs</Button>
            </Card>
        );
    }

    const renderProcessing = () => {
        const job = DEMO_JOBS.find((j) => j.briefId === brief.id);
        const stepLabel = job
            ? JOB_STEP_LABELS[job.status] || job.currentStep || "Processing..."
            : "Processing...";

        return (
            <div style={{ textAlign: "center", padding: 80 }}>
                <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} />} />
                <h3 style={{ marginTop: 24 }}>Generating Brief for {brief.supplierName}</h3>
                <p style={{ color: "#666", marginTop: 8 }}>{stepLabel}</p>
            </div>
        );
    };

    const renderCompleted = () => (
        <div ref={targetRef}>
            <div
                ref={htmlContainerRef}
                dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(brief.briefHtml || "", {
                        ADD_TAGS: ["style"],
                        ADD_ATTR: ["target", "rel"],
                        FORCE_BODY: true,
                        WHOLE_DOCUMENT: false,
                    }),
                }}
            />
        </div>
    );

    return (
        <Card
            title={
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate("/superadmin/demo/brief")}
                    />
                    <span>{brief.supplierName}</span>
                </div>
            }
            extra={
                <Button onClick={() => toPDF()} type="primary">
                    Export PDF
                </Button>
            }
        >
            {brief.status === BriefStatus.COMPLETED && renderCompleted()}
            {brief.status === BriefStatus.PROCESSING && renderProcessing()}
        </Card>
    );
};
